import { uniqueBy } from 'lib/array/arrayUtils'
import { createMapboxMapHandle } from 'lib/maps/createMapboxMapHandle'
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import type mapboxgl from 'mapbox-gl'
import type { TripItemMapMarker } from 'tripPlanner/components/TripMaps/utils'
import { isMapItem, isPinnedItem, mapTripItemToMarker, panToIntoViewIfObstructed } from 'tripPlanner/components/TripMaps/utils'
import { useTrips } from 'tripPlanner/hooks/api/trip'
import { useTripItems } from 'tripPlanner/hooks/api/tripItem'
import type { GeoPlace } from 'tripPlanner/types/common'
import type { MarkerItem } from 'tripPlanner/types/tripItem'
import { TRIP_PLANNER_ALL_TRIPS_OPTION } from './searchResultsMapTripPlannerConstants'
import type { SelectedTrip } from './TripPlanner/TripPlannerToggle.types'
import { getOfferId } from './utils'

export default function useTripPlannerPins({
  map,
  markers,
  onDeselectOffers,
}: {
  map: mapboxgl.Map | null
  markers: Array<App.SearchMapMarker>
  onDeselectOffers: () => void
}) {
  const { data: trips } = useTrips()
  const { data: allItemsFromAllTrips, isFetched: isItemsFetched } = useTripItems()

  const [tripPlannerMarkers, setTripPlannerMarkers] = useState<{
    customItems: Array<TripItemMapMarker>
    LEitems: Array<TripItemMapMarker>
  }>({ customItems: [], LEitems: [] })

  const tripPlannerLEItemOfferIds = useMemo(() => new Set(allItemsFromAllTrips?.map((item) => 'code' in item && item.code)), [allItemsFromAllTrips])
  const searchMarkersOfferIds = useMemo(() => new Set(markers.map((marker) => getOfferId(marker.offers[0]))), [markers])

  const [selectedTrip, setSelectedTrip] = useState<SelectedTrip>({ name: TRIP_PLANNER_ALL_TRIPS_OPTION, tripId: undefined, items: [] })

  const updateTripPlannerMarkers = useCallback((mapBounds: mapboxgl.LngLatBounds) => {
    const eligibleItems = selectedTrip.items.filter(isMapItem)
    const pinnedItems = eligibleItems.filter(isPinnedItem).flatMap((pinnedItem) => mapTripItemToMarker(pinnedItem))
    const contains = (position: { lat: number; lng: number }) => mapBounds.contains([position.lng, position.lat])
    const customItems = pinnedItems
      .filter((marker) => marker.item.sourceType === 'Explore' || marker.item.sourceType === 'NA')
      .filter((marker) => contains(marker.position))

    const LEitems = pinnedItems
      .filter((marker) => marker.item.sourceType !== 'Explore' && marker.item.sourceType !== 'NA')
      .filter((marker) => contains(marker.position) && 'code' in marker.item && marker.item.code && !searchMarkersOfferIds.has(marker.item.code))

    setTripPlannerMarkers({
      customItems,
      LEitems,
    })
  }, [searchMarkersOfferIds, selectedTrip.items])

  const [dropDownVisible, setDropDownVisible] = useState(false)

  const toggleDropdownVisibility = useCallback((_e: React.MouseEvent<HTMLButtonElement> | undefined, overrideValue?: boolean) => {
    setDropDownVisible((prev) => overrideValue ?? !prev)
  }, [])

  const handleSelectTrip = useCallback((tripName: string, tripId?: string) => {
    // If there is no tripId then its all items we should show.
    const items = !tripId ?
      allItemsFromAllTrips ?? [] :
        uniqueBy(
          (allItemsFromAllTrips ?? []).filter((item): item is MarkerItem & { place: GeoPlace } =>
            item.tripId === tripId &&
            'place' in item &&
            'point' in item.place,
          ),
          (item) => `${item.place.point.lat},${item.place.point.lng}`,
        )
    setSelectedTrip({ name: tripName, tripId, items })
    toggleDropdownVisibility(undefined, false)
  }, [allItemsFromAllTrips, toggleDropdownVisibility])

  useEffect(() => {
    const mapBounds = map?.getBounds()

    if (mapBounds) {
      updateTripPlannerMarkers(mapBounds)
    }
  }, [map, selectedTrip, updateTripPlannerMarkers])

  useEffect(() => {
    if (isItemsFetched) {
      handleSelectTrip(TRIP_PLANNER_ALL_TRIPS_OPTION)
    }
  }, [handleSelectTrip, isItemsFetched])

  const [selectedTripItem, setSelectedTripItem] = useState<MarkerItem | null>(null)

  const deselectTripItem = useCallback(() => {
    setSelectedTripItem(null)
  }, [])

  const tripItemCardSingleRef = useRef<HTMLDivElement | null>(null)

  const LEandCustomTripPlannerMarkers = useMemo(() =>
    [...tripPlannerMarkers.customItems, ...tripPlannerMarkers.LEitems]
      .filter((marker) => 'place' in marker.item ? marker.item.place : marker.item.startPlace),
  [tripPlannerMarkers.LEitems, tripPlannerMarkers.customItems])

  const canShowTripPlannerToggle = (trips?.length ?? 0) > 0 && (allItemsFromAllTrips?.length ?? 0) > 0

  const handleTripPlannerItemSelect = useCallback((item: MarkerItem) => {
    setSelectedTripItem(item)
    onDeselectOffers()
  }, [onDeselectOffers])

  useEffect(() => {
    if (selectedTripItem && map) {
      const place = 'place' in selectedTripItem ? selectedTripItem.place : selectedTripItem.startPlace
      panToIntoViewIfObstructed(
        createMapboxMapHandle(map),
        place.point,
        tripItemCardSingleRef.current,
      )
    }
  }, [selectedTripItem, map])

  return {
    tripPlannerLEItemOfferIds,
    dropDownVisible,
    deselectTripItem,
    LEandCustomTripPlannerMarkers,
    canShowTripPlannerToggle,
    handleTripPlannerItemSelect,
    updateTripPlannerMarkers,
    selectedTrip,
    selectedTripItem,
    handleSelectTrip,
    trips: trips ?? [],
    toggleDropdownVisibility,
    tripItemCardSingleRef,
  }
}
